import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { TwoFactorSetup } from './TwoFactorSetup';
import { Button } from './UI';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
  onLoginSuccess?: () => void;
}

type View = 'credentials' | '2fa_code' | '2fa_setup';

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onLoginSuccess,
}) => {
  const { t } = useTranslation();
  const { login, completeTwoFactorLogin } = useAuth();
  const [view, setView] = useState<View>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [pendingToken, setPendingToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const resetAndClose = () => {
    setEmail('');
    setPassword('');
    setTwoFactorCode('');
    setUseRecoveryCode(false);
    setPendingToken('');
    setView('credentials');
    setError('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const outcome = await login(email, password);

      if (outcome.status === 'success') {
        resetAndClose();
        onLoginSuccess?.();
      } else if (outcome.status === '2fa_required') {
        setPendingToken(outcome.pendingToken);
        setView('2fa_code');
      } else {
        setPendingToken(outcome.pendingToken);
        setView('2fa_setup');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:login.failedDefault'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await completeTwoFactorLogin(pendingToken, twoFactorCode, useRecoveryCode);
      resetAndClose();
      onLoginSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:twoFactor.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={resetAndClose}
      />

      {/* Modal - scrollt auf kleinen Screens mit, statt oben/unten abgeschnitten zu werden */}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain p-4 sm:items-center">
        <div className="card relative my-auto w-full max-w-md animate-slideInUp">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <img src="/canebase-icon.png" alt="canebase" className="h-9 w-auto object-contain" />
              <h1 className="text-2xl font-bold text-gradient">canebase</h1>
            </div>
            {view === 'credentials' && (
              <>
                <h2 className="text-xl font-bold text-dark-50">{t('auth:login.title')}</h2>
                <p className="text-sm text-dark-400 mt-1">{t('auth:login.subtitle')}</p>
              </>
            )}
            {view === '2fa_code' && (
              <>
                <h2 className="text-xl font-bold text-dark-50 flex items-center gap-2">
                  <ShieldCheck size={20} className="text-primary-400" />
                  {t('auth:twoFactor.codeTitle')}
                </h2>
                <p className="text-sm text-dark-400 mt-1">
                  {t('auth:twoFactor.codeSubtitle')}
                </p>
              </>
            )}
          </div>

          {view === 'credentials' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex gap-3">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-400">{t('auth:login.failedTitle')}</p>
                    <p className="text-xs text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label className="label-base">{t('auth:login.emailLabel')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth:login.emailPlaceholder')}
                    className="input-base pl-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="label-base">{t('auth:login.passwordLabel')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={'••••••••'}
                    className="input-base pl-10 pr-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full mt-6"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('auth:login.submitting')}
                  </>
                ) : (
                  t('auth:login.submit')
                )}
              </button>
            </form>
          )}

          {view === '2fa_code' && (
            <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex gap-3">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <div>
                <label className="label-base">
                  {useRecoveryCode ? t('auth:twoFactor.recoveryCodeLabel') : t('auth:twoFactor.authenticatorCodeLabel')}
                </label>
                <input
                  type="text"
                  inputMode={useRecoveryCode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder={useRecoveryCode ? 'xxxxx-xxxxx' : '123456'}
                  maxLength={useRecoveryCode ? 20 : 6}
                  className="input-base bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400 tracking-widest text-center"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <Button type="submit" fullWidth loading={loading}>
                {t('auth:twoFactor.confirm')}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  setTwoFactorCode('');
                  setError('');
                }}
                className="text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors w-full text-center"
              >
                {useRecoveryCode ? t('auth:twoFactor.useAuthenticatorCode') : t('auth:twoFactor.useRecoveryCode')}
              </button>
            </form>
          )}

          {view === '2fa_setup' && (
            <TwoFactorSetup
              pendingToken={pendingToken}
              onComplete={() => {
                resetAndClose();
                onLoginSuccess?.();
              }}
            />
          )}

          {view === 'credentials' && (
            <>
              {/* Divider */}
              <div className="divider my-6" />

              {/* Register Link */}
              <p className="text-center text-sm text-dark-400">
                {t('auth:login.noAccount')}{' '}
                <button
                  onClick={() => {
                    setEmail('');
                    setPassword('');
                    setError('');
                    onSwitchToRegister();
                  }}
                  className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
                >
                  {t('auth:login.registerNow')}
                </button>
              </p>
            </>
          )}

          {/* Close Button */}
          <button onClick={resetAndClose} className="icon-btn absolute right-2 top-2" aria-label={t('common:actions.close')}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};
